import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { sanitizeRichtextHtml } from '../../sanitize/sanitizeRichtextHtml.js';
import { marginFields, visibilityField } from '../_shared/blockFields.js';
import { marginStyle, num, schemeColor, str, visibilityCss } from '../_shared/blockRender.js';
import { MaterialIcon, MaterialSymbolsFont } from '../_shared/materialSymbols.js';
import { isThemeColorToken } from '../../theme/colorTokens.js';
/**
 * Shrine `icon-with-content` (preset "Icon with content", category "Icons") — config-by-config. A SINGLE
 * icon + heading + rich-text body in the product information column. The icon is a Google Material Symbols
 * NAME (the Shopify model — the merchant types `check_circle`, a custom image overrides it), so it shares
 * the exact icon engine as the `icon-with-text` block via {@link MaterialIcon}; the body is the block's own
 * sanitized richtext.
 *
 * SHRINE INVENTORY (16 settings — every one mapped):
 *  - `visibility` (Display on) → shared `visibilityField` + `visibilityCss`.
 *  Icon group:
 *  - `icon` (text → Material Symbols name) → `icon-name` field + {@link MaterialIcon}.
 *  - `filled_icon` (checkbox) → drives the FILL axis.
 *  - `image` (image_picker, "Custom icon") → `image` field, OVERRIDES the glyph.
 *  - `icon_size` (select xs/s/m/l/xl/xxl) → mapped to a px size via {@link ICON_SIZE_PX}.
 *  - `icon_position` (above / next-to-title / next-to-text) → the icon sits above the whole block, inline
 *    beside the heading line, or beside the heading+text column (Shrine's three layouts).
 *  - `icon_color` (accent-1/2 · background-1/2 · inverse · custom) → `schemeColor`, or `customIconColor` hex.
 *  - `custom_icon_color` (color) → hex applied when icon_color == custom (Shrine `visible_if`).
 *  Text group:
 *  - `icon_text_alignment` (left / center / right) → text-align (Shrine hides it for the "above" position).
 *  - `text_color` (accent-1/2 · background-1/2 · text · custom) → `schemeColor`, or `customTextColor` hex.
 *  - `custom_text_color` (color) → hex applied when text_color == custom (Shrine `visible_if`).
 *  - `title` (text) → the heading text.
 *  - `icon_heading_size` (select h5/h4/h3/h2 = XS/S/M/L) → mapped to a heading font-size via {@link HEADING_PX}.
 *  - `text` (richtext) → sanitized body, rendered via `sanitizeRichtextHtml`.
 *  - `text_size` (range 10–24 px, def 14) → body font-size.
 *  - `margin_top` / `margin_bottom` → shared `marginFields` + `marginStyle`.
 *
 * DEFERRED (Shopify-platform only): the `image_picker` field is rendered via our `image` field; the
 * Material Symbols glyph FONT is implemented (was Shrine's only platform dependency). `block.shopify_attributes`
 * and the preset `category` ("Icons") are Shopify section-renderer concepts with no authoring control here.
 * Shrine's `text_color: background-*` selects resolve to our `--aqx-scheme-*` tokens (no Shopify color-scheme
 * CSS classes exist for us). The Shrine schema "Icon"/"Text"/"Margins" headers are reproduced structurally
 * via the inspector groups, not as authoring controls.
 */
const ICON_COLOR_OPTIONS = [
    { value: 'accent-1', label: 'Accent 1' },
    { value: 'accent-2', label: 'Accent 2' },
    { value: 'background-1', label: 'Background 1' },
    { value: 'background-2', label: 'Background 2' },
    { value: 'inverse', label: 'Text' },
    { value: 'custom', label: 'Custom' },
];
const TEXT_COLOR_OPTIONS = [
    { value: 'accent-1', label: 'Accent 1' },
    { value: 'accent-2', label: 'Accent 2' },
    { value: 'background-1', label: 'Background 1' },
    { value: 'background-2', label: 'Background 2' },
    { value: 'text', label: 'Text' },
    { value: 'custom', label: 'Custom' },
];
const ICON_SIZE_OPTIONS = [
    { value: 'xs', label: 'Extra small' },
    { value: 's', label: 'Small' },
    { value: 'm', label: 'Medium' },
    { value: 'l', label: 'Large' },
    { value: 'xl', label: 'Extra large' },
    { value: 'xxl', label: 'Double extra large' },
];
const HEADING_SIZE_OPTIONS = [
    { value: 'h5', label: 'Extra small' },
    { value: 'h4', label: 'Small' },
    { value: 'h3', label: 'Medium' },
    { value: 'h2', label: 'Large' },
];
const ICON_POSITION_OPTIONS = [
    { value: 'above', label: 'Above title & text' },
    { value: 'next-to-title', label: 'Next to title' },
    { value: 'next-to-text', label: 'Next to text & title' },
];
const ALIGN_OPTIONS = [
    { value: 'left', label: 'Left' },
    { value: 'center', label: 'Center' },
    { value: 'right', label: 'Right' },
];
/** Shrine's `icon_size` keywords → a concrete px box (the theme's `--{x}` glyph scale). */
const ICON_SIZE_PX = {
    xs: 24,
    s: 32,
    m: 40,
    l: 56,
    xl: 72,
    xxl: 96,
};
/** Shrine's `icon_heading_size` (h5..h2 = XS..L) → a heading font-size in px. */
const HEADING_PX = {
    h5: 15,
    h4: 18,
    h3: 22,
    h2: 28,
};
export const iconWithContentBlockSchema = {
    type: 'icon-with-content',
    name: 'Icon with content',
    fields: [
        visibilityField,
        {
            id: 'icon',
            type: 'icon-name',
            label: 'Icon',
            group: 'Icon',
            default: 'check_circle',
            placeholder: 'check_circle',
            info: 'A Google Material Symbols name (e.g. check_circle, local_shipping). Search on fonts.google.com/icons.',
        },
        { id: 'filledIcon', type: 'checkbox', label: 'Filled icon', group: 'Icon', default: false },
        { id: 'image', type: 'image', label: 'Custom icon (overrides the icon)', group: 'Icon' },
        {
            id: 'iconSize',
            aiConfigurable: true,
            type: 'select',
            label: 'Icon size',
            group: 'Icon',
            default: 'm',
            options: ICON_SIZE_OPTIONS,
        },
        {
            id: 'iconPosition',
            aiConfigurable: true,
            type: 'select',
            label: 'Icon position',
            group: 'Icon',
            default: 'next-to-title',
            options: ICON_POSITION_OPTIONS,
        },
        {
            id: 'iconColor',
            aiConfigurable: true,
            type: 'select',
            label: 'Icon color',
            group: 'Icon',
            default: 'accent-1',
            options: ICON_COLOR_OPTIONS,
        },
        {
            id: 'customIconColor',
            type: 'color',
            label: 'Custom icon color',
            group: 'Icon',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: 'iconColor', equals: 'custom' },
        },
        {
            id: 'iconTextAlignment',
            aiConfigurable: true,
            type: 'select',
            label: 'Text alignment',
            group: 'Text',
            default: 'left',
            options: ALIGN_OPTIONS,
            visibleWhen: { field: 'iconPosition', notEquals: 'above' },
        },
        {
            id: 'textColor',
            type: 'select',
            label: 'Text color',
            group: 'Text',
            default: 'text',
            options: TEXT_COLOR_OPTIONS,
        },
        {
            id: 'customTextColor',
            type: 'color',
            label: 'Custom text color',
            group: 'Text',
            default: { ref: 'custom', value: '#121212' },
            visibleWhen: { field: 'textColor', equals: 'custom' },
        },
        {
            id: 'title',
            type: 'text',
            label: 'Heading',
            group: 'Text',
            default: 'Titre de l’icône',
            defaultKey: 'section.icon_title',
            // ÉCRIT PAR L'IA malgré sa traduction d'usine. Le titre d'une colonne de bénéfices n'est pas
            // une chaîne d'interface : c'est de l'argumentaire, propre à CE produit. Sans ce drapeau, le
            // relevé l'écarte (règle du dictionnaire 0050) et la page composée sort avec quatre colonnes
            // titrées « Icône et texte » — mesuré sur la page du fondateur, 25/08.
            aiWritable: true,
        },
        {
            id: 'iconHeadingSize',
            aiConfigurable: true,
            type: 'select',
            label: 'Heading size',
            group: 'Text',
            default: 'h3',
            options: HEADING_SIZE_OPTIONS,
            visibleWhen: { field: 'title', notEquals: '' },
        },
        {
            id: 'text',
            type: 'richtext',
            label: 'Text',
            group: 'Text',
            default: {
                format: 'html',
                html: '<p>Décrivez ici un avantage, un service ou une garantie qui rassure vos clients.</p>',
            },
        },
        {
            id: 'textSize',
            type: 'range',
            label: 'Text size',
            group: 'Text',
            min: 10,
            max: 24,
            step: 1,
            default: 14,
            unit: 'px',
        },
        ...marginFields(15, 15),
    ],
};
/** A stored `color` setting is a ColorValue; read its custom hex, else fall back to a literal. */
function colorHex(v, fallback) {
    if (v && typeof v === 'object') {
        const c = v;
        if (c.ref === 'custom' && typeof c.value === 'string')
            return c.value;
        // 0019 §4a: the THEME branch. Without it a { ref:'theme', token } silently fell through to the
        // frozen fallback, so a seller re-theming their store changed nothing here (allowlisted, finding #18).
        if (c.ref === 'theme' && typeof c.token === 'string' && isThemeColorToken(c.token))
            return `var(--aqx-theme-${c.token})`;
    }
    if (typeof v === 'string' && v.length > 0)
        return v;
    return fallback;
}
/** Map a Shrine alignment value to a CSS `text-align` keyword. */
function textAlign(v) {
    return v === 'center' || v === 'right' ? v : 'left';
}
/**
 * A single icon + heading + rich-text body. `iconPosition` switches between three Shrine layouts: ABOVE
 * (icon stacked over the whole block), NEXT-TO-TITLE (icon inline with the heading line only), NEXT-TO-TEXT
 * (icon beside the heading+text column). The icon size is driven through the shared `--aqx-icon-size` var so
 * {@link MaterialIcon} reflows without per-breakpoint markup. This is a GENERIC Shrine content block (used
 * in the product info column AND in the Custom Columns V2 grid), so it never depends on product state — it
 * renders from its own settings anywhere, and shows nothing only when it has no icon, heading nor body.
 */
const IconWithContentBlock = ({ block }) => {
    const bs = block.settings;
    const cls = `aqx-iwc-${block.id}`;
    const title = str(bs.title, '');
    const rt = bs.text;
    const bodyHtml = sanitizeRichtextHtml(rt?.format === 'html' ? rt.html : '');
    const image = bs.image;
    const iconName = str(bs.icon, '');
    const hasIcon = Boolean(image?.url) || iconName.trim().length > 0;
    // Nothing to show if there is neither a heading, body, nor icon.
    if (!hasIcon && title.trim().length === 0 && bodyHtml.trim().length === 0)
        return null;
    const position = str(bs.iconPosition, 'next-to-title');
    const iconSize = ICON_SIZE_PX[str(bs.iconSize, 'm')] ?? 40;
    const filled = bs.filledIcon === true;
    const iconColorSel = str(bs.iconColor, 'accent-1');
    const iconColor = iconColorSel === 'custom'
        ? colorHex(bs.customIconColor, 'var(--aqx-theme-accent)')
        : schemeColor(iconColorSel);
    const textColorSel = str(bs.textColor, 'text');
    const textColor = textColorSel === 'custom' ? colorHex(bs.customTextColor, '#121212') : schemeColor(textColorSel);
    const align = position === 'above' ? 'left' : textAlign(str(bs.iconTextAlignment, 'left'));
    const headingPx = HEADING_PX[str(bs.iconHeadingSize, 'h3')] ?? 22;
    const textPx = num(bs.textSize, 14);
    const icon = hasIcon ? (_jsx("span", { className: `${cls}__icon`, style: { flex: '0 0 auto', lineHeight: 0 }, children: _jsx(MaterialIcon, { name: iconName, filled: filled, image: image, size: iconSize, color: iconColor }) })) : null;
    // Shrine layout matrix:
    //  - above        → icon block stacked over the text column (whole-block column).
    //  - next-to-title→ icon inline INSIDE the heading line (icon + title share a row, body below).
    //  - next-to-text → icon beside the heading+text column (icon is a sibling of the text column).
    const rootIsRow = position === 'next-to-text';
    const rootRule = `.${cls}{display:flex;flex-direction:${rootIsRow ? 'row' : 'column'};` +
        `align-items:${rootIsRow ? 'flex-start' : 'stretch'};` +
        `gap:var(--aqx-theme-space-3, 12px);--aqx-icon-size:${iconSize}px;font-family:var(--aqx-theme-font)}`;
    const textColRule = `.${cls}__text-col{display:flex;flex-direction:column;gap:var(--aqx-theme-space-2, 8px);flex:1 1 auto;min-inline-size:0;text-align:${align}}`;
    const titleRowRule = `.${cls}__title{margin:0;display:flex;align-items:center;` +
        `justify-content:${align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start'};` +
        `gap:var(--aqx-theme-space-2, 8px);` +
        `font-family:var(--aqx-theme-heading-font, var(--aqx-theme-font));` +
        `font-size:${headingPx}px;font-weight:var(--aqx-theme-fw-semibold, 600);` +
        `line-height:var(--aqx-theme-lh-snug, 1.25);color:${textColor}}`;
    const bodyRule = `.${cls}__body{font-size:${textPx}px;color:${textColor};line-height:var(--aqx-theme-lh, 1.5)}` +
        `.${cls}__body p{margin:0}.${cls}__body p+p{margin-top:var(--aqx-theme-space-2, 8px)}`;
    const css = rootRule + textColRule + titleRowRule + bodyRule + visibilityCss(cls, bs.visibility);
    const style = marginStyle(bs, 15, 15);
    // `above` → icon above the text column; `next-to-text` → icon as a sibling beside the text column;
    // `next-to-title` → icon INSIDE the heading line.
    const iconInTitle = position === 'next-to-title';
    const iconAboveOrBeside = position === 'above' || position === 'next-to-text';
    return (_jsxs(_Fragment, { children: [_jsx(MaterialSymbolsFont, {}), _jsx("style", { dangerouslySetInnerHTML: { __html: css } }), _jsxs("div", { className: cls, style: style, children: [iconAboveOrBeside ? icon : null, _jsxs("div", { className: `${cls}__text-col`, children: [title.trim().length > 0 ? (_jsxs("h3", { className: `${cls}__title`, children: [iconInTitle ? icon : null, _jsx("span", { children: title })] })) : null, bodyHtml.trim().length > 0 ? (_jsx("div", { className: `${cls}__body`, dangerouslySetInnerHTML: { __html: bodyHtml } })) : null] })] })] }));
};
export const iconWithContentBlock = {
    type: 'icon-with-content',
    schema: iconWithContentBlockSchema,
    Component: IconWithContentBlock,
    meta: { label: 'Icon with content' },
};
//# sourceMappingURL=index.js.map